import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { useLenis } from '@/hooks/useLenis';
import { FadeSection } from '@/components/shared/FadeSection';
import { Check, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { track } from '@/lib/analytics';
import '@/styles/report.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TARGETS = [
  { value: 'profile', label: 'A profile', mapped: 'user', requiresIdentifier: true },
  { value: 'conversation', label: 'A conversation', mapped: 'message', requiresIdentifier: true },
  { value: 'content', label: 'Something I saw on NYMPHÉ', mapped: 'content', requiresIdentifier: false },
  { value: 'other', label: 'Something else', mapped: 'other', requiresIdentifier: false },
];

const REASONS = [
  'Harassment or abusive behavior',
  'Unsafe or threatening conduct',
  'Impersonation',
  'Spam or scam',
  'Pressure to move off-platform',
  'Sexual misconduct',
  'Something else',
];

const ReportPage = () => {
  useLenis();

  const [searchParams] = useSearchParams();
  const requestedTarget = searchParams.get('target');
  const initialTarget = TARGETS.some((t) => t.value === requestedTarget)
    ? requestedTarget
    : '';

  const [target, setTarget] = useState(initialTarget);
  const [whoInvolved, setWhoInvolved] = useState('');
  const [reasons, setReasons] = useState([]);
  const [details, setDetails] = useState('');
  const [consentTruthful, setConsentTruthful] = useState(false);
  const [consentReview, setConsentReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const targetMeta = TARGETS.find((t) => t.value === target);
  const requiresIdentifier = !!targetMeta?.requiresIdentifier;

  const isReady =
    !!target &&
    (!requiresIdentifier || whoInvolved.trim().length > 0) &&
    reasons.length > 0 &&
    details.trim().length >= 4 &&
    consentTruthful &&
    consentReview;

  const toggleReason = (reason) => {
    setReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isReady || submitting) return;

    setSubmitting(true);
    setErrorMsg('');

    const composedDescription =
      `Reasons: ${reasons.join(', ')}\n\nDetails:\n${details.trim()}`;

    try {
      const response = await axios.post(`${API}/report`, {
        report_type: targetMeta?.mapped || 'other',
        description: composedDescription,
        who_involved: whoInvolved.trim() || null,
        screenshot_url: null,
      });

      if (response?.data?.success) {
        // Privacy-safe analytics: anonymised metadata only.
        // No username, no details, no reason labels, no PII.
        try {
          track('report_submitted', {
            target_type: targetMeta?.value || 'other',
            reason_count: reasons.length,
          });
        } catch (analyticsErr) {
          if (process.env.NODE_ENV === 'development') {
             
            console.warn('[analytics] report_submitted failed:', analyticsErr);
          }
        }
        setSubmitted(true);
      } else {
        setErrorMsg(
          response?.data?.message ||
            "We couldn't send your report just now. Please try again in a moment."
        );
      }
    } catch (err) {
      setErrorMsg(
        "We couldn't send your report just now. Please try again in a moment, or email us if it keeps failing."
      );
    }

    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="report-page" data-testid="report-page">
        <div className="noise-overlay" />

        <section className="report-success">
          <div className="report-success-inner">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="report-success-icon"
            >
              <Check size={28} />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="report-success-headline"
            >
              Report received
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="report-success-body"
            >
              <p>Thank you for letting us know.</p>
              <p>Our safety team reviews every report. We'll take action where needed.</p>
              <p className="report-success-fineprint">
                Your report stays private. The person you reported will not be told it was you.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="report-nav-row"
            >
              <Link to="/safety" className="report-nav-link" data-testid="report-back-safety">
                <ArrowLeft size={14} />
                <span>Back to Safety</span>
              </Link>
              <Link to="/" className="report-nav-link report-nav-link-muted" data-testid="report-back-home">
                Back to NYMPHÉ
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="report-page" data-testid="report-page">
      <div className="noise-overlay" />

      {/* Header */}
      <section className="report-header">
        <div className="report-header-inner">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="report-headline"
          >
            Report something
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="report-subtext"
          >
            <p>If something felt off, you can tell us.</p>
            <p>You don't need to over-explain.</p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="report-form-section">
        <form onSubmit={handleSubmit} className="report-form" noValidate>

          {/* Section 1 — Target */}
          <FadeSection className="report-field-group">
            <label className="report-label">Who or what are you reporting?</label>
            <div className="report-options">
              {TARGETS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`report-option ${target === option.value ? 'selected' : ''}`}
                  onClick={() => setTarget(option.value)}
                  data-testid={`report-target-${option.value}`}
                  aria-pressed={target === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {requiresIdentifier && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="report-conditional"
              >
                <label className="report-sublabel" htmlFor="report-who">
                  Username or profile link
                  <span className="report-required-tag">Required</span>
                </label>
                <input
                  id="report-who"
                  type="text"
                  className="report-input"
                  placeholder="e.g. @lavender or a profile link"
                  value={whoInvolved}
                  onChange={(e) => setWhoInvolved(e.target.value)}
                  data-testid="report-who"
                  autoComplete="off"
                />
                <p className="report-hint">
                  This helps our team locate the {target === 'profile' ? 'profile' : 'conversation'} quickly. We never tell them it was you.
                </p>
              </motion.div>
            )}
          </FadeSection>

          {/* Section 2 — Reasons (multi-select) */}
          <FadeSection delay={0.05} className="report-field-group">
            <label className="report-label">
              What happened?
              <span className="report-optional-tag">Select all that apply</span>
            </label>
            <div className="report-checkbox-grid">
              {REASONS.map((reason) => {
                const checked = reasons.includes(reason);
                const id = `reason-${reason.replace(/\s+/g, '-').toLowerCase()}`;
                return (
                  <label
                    key={reason}
                    htmlFor={id}
                    className={`report-checkbox-row ${checked ? 'checked' : ''}`}
                    data-testid={`report-reason-${reason.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      className="report-checkbox-input"
                      checked={checked}
                      onChange={() => toggleReason(reason)}
                    />
                    <span className="report-checkbox-box" aria-hidden="true">
                      {checked && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="report-checkbox-text">{reason}</span>
                  </label>
                );
              })}
            </div>
          </FadeSection>

          {/* Section 3 — Details */}
          <FadeSection delay={0.1} className="report-field-group">
            <label className="report-label" htmlFor="report-details">
              Anything else we should know?
            </label>
            <textarea
              id="report-details"
              className="report-textarea"
              placeholder="A few sentences is enough. Share only what you're comfortable with."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={6}
              data-testid="report-description"
            />
          </FadeSection>

          {/* Section 4 — Evidence note (replaces upload) */}
          <FadeSection delay={0.15} className="report-evidence-note">
            <p>If needed, we may request additional context to support your report.</p>
          </FadeSection>

          {/* Section 5 — Consent */}
          <FadeSection delay={0.2} className="report-field-group report-consent-group">
            <label
              className={`report-checkbox-row report-consent-row ${consentTruthful ? 'checked' : ''}`}
              htmlFor="consent-truthful"
              data-testid="report-consent-truthful-label"
            >
              <input
                id="consent-truthful"
                type="checkbox"
                className="report-checkbox-input"
                checked={consentTruthful}
                onChange={(e) => setConsentTruthful(e.target.checked)}
                data-testid="report-consent-truthful"
              />
              <span className="report-checkbox-box" aria-hidden="true">
                {consentTruthful && <Check size={12} strokeWidth={3} />}
              </span>
              <span className="report-checkbox-text">
                I'm reporting this in good faith and the information I've shared is true to the best of my knowledge.
              </span>
            </label>

            <label
              className={`report-checkbox-row report-consent-row ${consentReview ? 'checked' : ''}`}
              htmlFor="consent-review"
              data-testid="report-consent-review-label"
            >
              <input
                id="consent-review"
                type="checkbox"
                className="report-checkbox-input"
                checked={consentReview}
                onChange={(e) => setConsentReview(e.target.checked)}
                data-testid="report-consent-review"
              />
              <span className="report-checkbox-box" aria-hidden="true">
                {consentReview && <Check size={12} strokeWidth={3} />}
              </span>
              <span className="report-checkbox-text">
                I understand NYMPHÉ's safety team will review this report and may take action, including limiting or removing accounts.
              </span>
            </label>

            <p className="report-misuse-disclaimer">
              False or malicious reports may affect account access.
            </p>
          </FadeSection>

          {/* Privacy reassurance */}
          <FadeSection delay={0.22} className="report-privacy-block">
            <p className="report-privacy-title">Your report stays private.</p>
            <ul className="report-privacy-list">
              <li>It is never shared with the person you're reporting.</li>
              <li>It's only seen by NYMPHÉ's safety team.</li>
              <li>You can keep using NYMPHÉ as normal while we review it.</li>
            </ul>
          </FadeSection>

          {/* Error fallback */}
          {errorMsg && (
            <div className="report-error" data-testid="report-error" role="alert">
              {errorMsg}
            </div>
          )}

          {/* Submit */}
          <FadeSection delay={0.25} className="report-submit-group">
            <button
              type="submit"
              className="report-submit-btn"
              disabled={!isReady || submitting}
              data-testid="report-submit"
            >
              {submitting ? 'Sending…' : 'Submit report'}
            </button>
            {!isReady && !submitting && (
              <p className="report-submit-hint" data-testid="report-submit-hint">
                Please complete the required fields and confirm both checkboxes above.
              </p>
            )}
          </FadeSection>
        </form>
      </section>

      {/* Emergency disclaimer */}
      <section className="report-safety">
        <FadeSection>
          <div className="report-safety-inner" data-testid="report-emergency">
            <p className="report-safety-text">
              NYMPHÉ is not an emergency service. If you are in immediate danger, contact your local emergency services.
            </p>
          </div>
        </FadeSection>
      </section>

      {/* Bottom navigation */}
      <section className="report-nav-section">
        <FadeSection className="report-nav-row report-nav-row-bottom">
          <Link to="/safety" className="report-nav-link" data-testid="report-back-safety">
            <ArrowLeft size={14} />
            <span>Back to Safety</span>
          </Link>
          <Link to="/" className="report-nav-link report-nav-link-muted" data-testid="report-back-home">
            Back to NYMPHÉ
          </Link>
        </FadeSection>
      </section>
    </div>
  );
};

export default ReportPage;
